'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User, Search, ChevronDown, Loader2, X } from 'lucide-react';
import { authService } from '@/lib/authService';
import useKeyboardSelectableList from '@/hooks/useKeyboardSelectableList';

export interface EmployeeOption {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  name?: string | null;
  employee_number?: string | null;
  department?: string | null;
  job_title?: string | null;
  email?: string | null;
  employment_status?: string | null;
}

export interface EmployeeLookupProps {
  /** Currently selected employee id (or null). */
  value: number | null;
  /** Called with the selected employee id and record (null on clear). */
  onChange: (id: number | null, employee?: EmployeeOption | null) => void;
  /** Optional pre-loaded employee list — when provided, searches filter
   *  locally instead of hitting /api/v1/employees. */
  employees?: EmployeeOption[];
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  /** Employee ids to hide from results (e.g. the subject of a review when
   *  picking a reviewer). */
  excludeIds?: number[];
  /** Employment status filter for remote search (default 'active'). */
  employmentStatus?: string;
  /** When true, a clear (×) button is shown on the selected value even for
   *  required fields — parents should treat clear as "unset". */
  allowClear?: boolean;
  /** Display text for the current selection when the employee record isn't
   *  loaded (e.g. editing a record whose employee list wasn't fetched). */
  valueLabel?: string;
  className?: string;
}

function employeeDisplayName(emp: EmployeeOption): string {
  return (
    `${emp.first_name || ''} ${emp.last_name || ''}`.trim() ||
    emp.full_name ||
    emp.name ||
    emp.employee_number ||
    `Employee #${emp.id}`
  );
}

function employeeSubtitle(emp: EmployeeOption): string {
  return [emp.employee_number, emp.department, emp.job_title].filter(Boolean).join(' · ');
}

/**
 * Searchable employee dropdown — the workforce equivalent of
 * UnifiedContactLookup's contact search. Replaces free-text employee_id
 * inputs: type to search (remote via /api/v1/employees, or local when
 * `employees` is provided), click or arrow-key + Enter to select, × to clear.
 */
export default function EmployeeLookup({
  value,
  onChange,
  employees: localEmployees,
  label,
  placeholder = 'Search employees...',
  required = false,
  disabled = false,
  excludeIds,
  employmentStatus = 'active',
  allowClear = true,
  valueLabel,
  className = '',
}: EmployeeLookupProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<EmployeeOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeOption | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const latestRequestId = useRef(0);

  const getAuthToken = useCallback(() => authService.getTokens()?.access_token || '', []);

  const excluded = useCallback(
    (emp: EmployeeOption) => !!excludeIds?.includes(Number(emp.id)),
    [excludeIds],
  );

  // Resolve the display record for a pre-set value (edit forms).
  useEffect(() => {
    if (!value) {
      setSelectedEmployee(null);
      return;
    }
    const fromList = localEmployees?.find((e) => Number(e.id) === Number(value));
    if (fromList) {
      setSelectedEmployee(fromList);
      return;
    }
    if (selectedEmployee?.id === Number(value)) return;

    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/v1/employees/${value}`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const emp = (data.data || data.employee || data) as EmployeeOption;
        if (!cancelled && emp?.id) setSelectedEmployee(emp);
      } catch {
        // Fall back to showing the raw id via valueLabel/placeholder
      }
    })();
    return () => { cancelled = true; };
  }, [value, localEmployees, getAuthToken, selectedEmployee?.id]);

  const performSearch = useCallback(
    async (term: string) => {
      if (localEmployees) {
        const q = term.trim().toLowerCase();
        const filtered = localEmployees.filter((e) => {
          if (excluded(e)) return false;
          if (!q) return true;
          return [e.first_name, e.last_name, e.full_name, e.name, e.employee_number, e.department, e.job_title, e.email]
            .filter(Boolean)
            .some((f) => String(f).toLowerCase().includes(q));
        });
        setResults(filtered.slice(0, 25));
        setLoading(false);
        return;
      }

      const requestId = ++latestRequestId.current;
      try {
        setLoading(true);
        const params = new URLSearchParams({ limit: '25' });
        if (term.trim()) params.set('search', term.trim());
        if (employmentStatus) params.set('employment_status', employmentStatus);
        const res = await fetch(`/api/v1/employees?${params}`, {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        });
        if (requestId !== latestRequestId.current) return;
        if (res.ok) {
          const data = await res.json();
          const list: EmployeeOption[] = (data.data || data.employees || []) as EmployeeOption[];
          setResults(list.filter((e) => !excluded(e)));
        } else {
          setResults([]);
        }
      } catch (error) {
        if (requestId === latestRequestId.current) {
          console.error('Employee lookup failed:', error);
          setResults([]);
        }
      } finally {
        if (requestId === latestRequestId.current) setLoading(false);
      }
    },
    [localEmployees, employmentStatus, getAuthToken, excluded],
  );

  // Debounce remote/local search
  useEffect(() => {
    if (!isOpen) return;
    const t = setTimeout(() => void performSearch(search), 200);
    return () => clearTimeout(t);
  }, [search, isOpen, performSearch]);

  // Click-outside closes the dropdown
  useEffect(() => {
    const handleClickOutside = (e: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('pointerdown', handleClickOutside, true);
    return () => document.removeEventListener('pointerdown', handleClickOutside, true);
  }, []);

  const handleSelect = (emp: EmployeeOption) => {
    onChange(Number(emp.id), emp);
    setSelectedEmployee(emp);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = () => {
    onChange(null, null);
    setSelectedEmployee(null);
    setSearch('');
    inputRef.current?.focus();
  };

  const { activeIndex, registerItemRef, handleKeyDown } = useKeyboardSelectableList<EmployeeOption>({
    isOpen,
    items: results,
    onSelect: handleSelect,
    onClose: () => setIsOpen(false),
  });

  const selectedName = selectedEmployee
    ? employeeDisplayName(selectedEmployee)
    : (valueLabel || (value ? `Employee #${value}` : ''));

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      <div ref={containerRef} className="relative">
        {value ? (
          /* Selected state — mirrors the contact card in UnifiedContactLookup */
          <div className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <User className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="font-medium text-gray-900 dark:text-gray-100 truncate">{selectedName}</span>
              {selectedEmployee && employeeSubtitle(selectedEmployee) && (
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {employeeSubtitle(selectedEmployee)}
                </span>
              )}
            </div>
            {allowClear && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="ml-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded"
                title="Clear selection"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : (
          /* Search state — search input + dropdown results */
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                disabled={disabled}
                onFocus={() => {
                  setIsOpen(true);
                  void performSearch(search);
                }}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setIsOpen(true);
                }}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                required={required && !value}
                className="w-full pl-9 pr-9 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 disabled:opacity-50"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {loading ? (
                  <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </div>
            </div>

            {isOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                {loading && results.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    Searching…
                  </div>
                ) : results.length === 0 ? (
                  <div className="px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    {search.trim() ? 'No employees found' : 'No employees available'}
                  </div>
                ) : (
                  results.map((emp, i) => (
                    <button
                      key={emp.id}
                      type="button"
                      ref={registerItemRef(i)}
                      onClick={() => handleSelect(emp)}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-3 transition-colors ${
                        i === activeIndex
                          ? 'bg-teal-50 dark:bg-teal-900/30'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="w-7 h-7 rounded-full bg-teal-100 dark:bg-teal-900/40 flex items-center justify-center shrink-0">
                        <User className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                          {employeeDisplayName(emp)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {employeeSubtitle(emp) || `#${emp.id}`}
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
